import { useQuery } from '@tanstack/react-query';
import { format } from 'date-fns';
import { LayoutGrid, Armchair, DollarSign } from 'lucide-react';
import apiClient from '@/services/api';
import type { OperationsReport } from '@/types/api';
import { KPICard } from '@/components/reports/KPICard';
import { ReportFrame, SimpleTable } from '@/components/reports/ReportFrame';
import type { ReportViewProps } from '@/components/reports/reportTypes';
import { buildGenericCsv, downloadCsv, printGenericReport, type ReportSection } from '@/utils/reportExport';

/** Shop Floor / Table Sales: revenue and turns by floor and by table. */
export default function ShopFloorReport({ filters }: ReportViewProps) {
  const { selectedRestaurantId, startDate, endDate, formatCurrency, selectedRestaurant } = filters;

  const { data, isLoading, isError, refetch } = useQuery<OperationsReport>({
    queryKey: ['operations-report', selectedRestaurantId, startDate, endDate],
    queryFn: () => apiClient.getOperationsReport(selectedRestaurantId!, startDate, endDate),
    enabled: !!selectedRestaurantId,
  });

  const floors = data?.floorUtilization ?? [];
  const tables = data?.tableTurns ?? [];
  const totalRevenue = floors.reduce((a, f) => a + Number(f.revenue), 0);
  const meta = () => ({ restaurantName: selectedRestaurant?.name ?? '', startDate, endDate, formatCurrency });

  const sections = (): ReportSection[] => [
    { heading: 'By Floor', headers: ['Floor', 'Tables', 'Turns', 'Revenue'], rows: floors.map((f) => [f.floorName, f.tableCount, f.turns, formatCurrency(f.revenue)]) },
    { heading: 'By Table', headers: ['Table', 'Floor', 'Turns', 'Covers', 'Revenue'], rows: tables.map((t) => [t.tableName || String(t.tableId), t.floorName ?? '—', t.turns, t.covers, formatCurrency(t.revenue)]) },
  ];
  const kpis = (): [string, string][] => [
    ['Floors', floors.length.toLocaleString()],
    ['Tables Used', tables.length.toLocaleString()],
    ['Revenue', formatCurrency(totalRevenue)],
  ];

  return (
    <ReportFrame
      title="Shop Floor / Table Sales"
      description="Revenue and turns by floor and by table across the range (dine-in sessions)."
      hasRestaurant={!!selectedRestaurantId}
      isLoading={isLoading}
      isError={isError}
      onRetry={() => refetch()}
      isEmpty={floors.length === 0 && tables.length === 0}
      exportDisabled={!data}
      onExportCsv={() => data && downloadCsv(`shop-floor-${format(new Date(), 'yyyy-MM-dd')}.csv`, buildGenericCsv('Shop Floor / Table Sales', meta(), kpis(), sections()))}
      onExportPdf={() => data && printGenericReport('Shop Floor / Table Sales Report', meta(), kpis(), sections())}
    >
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <KPICard label="Floors" value={floors.length.toLocaleString()} icon={LayoutGrid} palette="blue" />
        <KPICard label="Tables Used" value={tables.length.toLocaleString()} icon={Armchair} palette="purple" />
        <KPICard label="Revenue" value={formatCurrency(totalRevenue)} icon={DollarSign} palette="green" />
      </div>
      <div className="card"><div className="card-body">
        <h3 className="text-lg font-semibold mb-4">By Floor</h3>
        <SimpleTable headers={['Floor', 'Tables', 'Turns', 'Revenue']}
          rows={floors.map((f) => [f.floorName, f.tableCount, f.turns, formatCurrency(f.revenue)])} />
      </div></div>
      <div className="card"><div className="card-body">
        <h3 className="text-lg font-semibold mb-4">By Table</h3>
        <SimpleTable headers={['Table', 'Floor', 'Turns', 'Covers', 'Revenue']}
          rows={tables.map((t) => [t.tableName || String(t.tableId), t.floorName ?? '—', t.turns, t.covers, formatCurrency(t.revenue)])} />
      </div></div>
    </ReportFrame>
  );
}
